<script setup>
import OrderItem from '@/components/general/OrderItem.vue'
import StatsCard from '@/components/general/StatsCard.vue'
import { useBusinessStore } from '@/stores/businessStore'
import { storeToRefs } from 'pinia'
import { onMounted, watch } from 'vue'
import { ROUTES } from '@/router/routeNames'
import ToggleSwitch from 'primevue/toggleswitch';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '@/stores/authStore';
import { formatCurrency } from '@/utils';
import DateFilter from '@/components/general/DateFilter.vue';
const toast = useToast()
const businessStore = useBusinessStore()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const { recentOrders, business, stats, isLoading, availabilityError } = storeToRefs(businessStore)
const toggleAvailability = async () => {
  try {
    await businessStore.toggleAvailability()
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Availability toggled successfully',
      life: 3000,
    })
  } catch (error) {
    console.error('Error toggling availability:', error)
  }
}
watch(availabilityError, (error) => {
  if (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error,
      life: 3000,
    })
  }
})
onMounted(() => {
  businessStore.fetchDashboardData()
})

const handleDateFilter = (dateFilter, startDate, endDate) => {
  businessStore.fetchDashboardData(dateFilter, startDate, endDate)
}
</script>

<template>
  <div class="sm:py-12" v-if="!isLoading">
    <div class="sm:px-6 lg:px-8">
      <div class="mb-6 text-2xl font-bold text-gray-900 dark:text-white">Welcome {{ user?.name }}</div>

      <DateFilter @dateFilter="handleDateFilter" />
      <!-- Business Stats -->
      <div class="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3" v-if="stats">
        <StatsCard title="Total Products" :value="stats.total_products || 0" />
        <StatsCard title="Total Orders" :value="stats.total_orders || 0" />
        <StatsCard title="Total Sales" :value="formatCurrency(stats.total_sales || 0)" />
        <div
          class="overflow-hidden p-6 bg-white shadow-xl dark:text-white dark:bg-gray-800 sm:rounded-lg">
          <h3 class="text-sm font-medium text-gray-500">Store Status</h3>
          <div class="flex justify-between items-center">
            <p class="mt-2 text-3xl font-semibold">{{ stats.is_active ? 'Active' : 'Inactive' }}</p>
            <ToggleSwitch v-model="stats.is_active" @change="toggleAvailability" />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">


        <!-- Quick Actions -->
        <div class="overflow-hidden p-6 bg-white shadow-xl dark:bg-gray-800 sm:rounded-lg">
          <h3 class="mb-4 text-lg font-semibold dark:text-white">Quick Actions</h3>
          <div class="space-y-4">
            <RouterLink
              :to="{ name: ROUTES.PRODUCT_CREATE }"
              class="block p-4 w-full text-center text-white bg-indigo-600 rounded-lg transition hover:bg-indigo-700">
              Add New Product
            </RouterLink>
            <RouterLink
              :to="{ name: ROUTES.PRODUCTS }"
              class="block p-4 w-full text-center text-white bg-blue-600 rounded-lg transition hover:bg-blue-700">
              View All Products
            </RouterLink>
            <RouterLink
              v-if="business"
              :to="{ name: ROUTES.BUSINESS_EDIT, params: { id: business.uuid } }"
              class="block p-4 w-full text-center text-white bg-green-600 rounded-lg transition hover:bg-green-700">
              Edit Business Profile
            </RouterLink>
          </div>
        </div>
        <!-- Recent Orders -->
        <div class="overflow-hidden p-6 bg-white shadow-xl dark:bg-gray-800 sm:rounded-lg">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold dark:text-white">Recent Orders</h3>
            <RouterLink
              :to="{ name: ROUTES.DASHBOARD_ORDERS }"
              class="text-sm text-indigo-600 hover:text-indigo-800">
              View All
            </RouterLink>
          </div>
          <div v-if="recentOrders.length > 0">
            <OrderItem v-for="order in recentOrders" :key="order.id" :order="order" />
          </div>
          <div v-else class="text-gray-600 dark:text-white">No recent orders</div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="flex justify-center items-center py-10">
    <div class="w-12 h-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
  </div>
</template>
